import { Download, DocumentExport } from "@carbon/icons-react";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { z } from "zod";

import { campaignGroupsQueryOptions } from "@/api/campaignGroups";
import { formSummariesQueryOptions } from "@/api/formSummaries";
import { listCampaignsQueryOptions } from "@/api/listCampaigns";
import { CampaignPublishAction } from "@/components/admin/campaignPublishAction";
import { CampaignReportHeader } from "@/components/admin/campaignReport/campaignReportHeader";
import { PersonReport } from "@/components/admin/campaignReport/personReport";
import { ReportPeopleList } from "@/components/admin/campaignReport/reportPeopleList";
import { Breadcrumbs } from "@/components/breadcrumbs";
import { Button, buttonVariants } from "@/components/input/button";
import { Select } from "@/components/input/select";
import { usePrinting } from "@/hooks/usePrinting";
import { Campaign } from "@/schemas/campaign";
import { CampaignIdSchema } from "@/schemas/campaignId";
import { EmployeeId, EmployeeIdSchema } from "@/schemas/employeeId";
import {
  CAMPAIGN_REPORT_CSV_HEADER,
  campaignReportCsvRows,
  ReportViewMode,
  toAdminCampaignReport,
} from "@/util/adminCampaignReport";
import { reportStatusOf } from "@/util/campaignReport";
import { csvFileName, downloadCsv, toCsv } from "@/util/csvExport";
import { lenient } from "@/util/lenient";

const CampaignReportSearchSchema = z.object({
  person: lenient(EmployeeIdSchema),
  form: lenient(z.string().min(1)),
});

// Admin campaign report (specs/023-admin-campaign-report): every person, form and answer of one
// campaign. `.index` keeps the member report (`$campaignId.$employeeId.tsx`) a sibling, not a child.
export const Route = createFileRoute("/dashboard/_authenticated/admin/reports/$campaignId/")({
  component: RouteComponent,
  params: {
    parse: (parameters) => z.object({ campaignId: z.preprocess(Number, CampaignIdSchema) }).parse(parameters),
  },
  validateSearch: CampaignReportSearchSchema,
  errorComponent: () => <div className="text-body2 text-error-300">Invalid link: this campaign report doesn't exist.</div>,
  staticData: { pageWidth: "wide" },
  // No breadcrumb of its own: the `reports` layout adds "Reports", the page appends the campaign.
});

function RouteComponent() {
  const { campaignId } = Route.useParams();
  const match = Route.useMatch();

  // From the list (shared cache with the Reports cards, invalidated by publish/unpublish): it carries
  // every evaluation with its answers (`campaignDetailQueryOptions` targets a path the backend lacks).
  const campaignsQuery = useQuery(listCampaignsQueryOptions());
  const campaign = campaignsQuery.data?.content.find((item) => item.id === campaignId);

  if (campaignsQuery.isPending) {
    return <ReportSkeleton />;
  }

  if (campaignsQuery.isError) {
    return (
      <div className="flex flex-wrap items-center gap-4">
        <span className="text-body2 text-error-300">Couldn't load this report.</span>
        <button
          className={buttonVariants({ size: "sm", className: "min-h-11 lg:min-h-0" })}
          onClick={() => void campaignsQuery.refetch()}
          type="button"
        >
          Retry
        </button>
      </div>
    );
  }

  if (!campaign) {
    return (
      <div className="flex flex-col items-start gap-3">
        <div className="text-body2 text-neutral-600">Campaign not found.</div>
        <Link
          className="
            flex min-h-11 items-center text-body2 font-semibold
            text-primary-default
            hover:underline
            lg:min-h-0
          "
          to="/dashboard/admin/reports"
        >
          Back to Reports
        </Link>
      </div>
    );
  }

  return (
    <>
      <div className="print:hidden">
        <Breadcrumbs crumbsToAppend={[{ label: campaign.name, path: match.pathname }]} />
      </div>
      <CampaignReport campaign={campaign} />
    </>
  );
}

function CampaignReport({ campaign }: { campaign: Campaign }) {
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const groupsQuery = useQuery(campaignGroupsQueryOptions(campaign.id));
  const { data: forms } = useQuery(formSummariesQueryOptions());
  const [mode, setMode] = useState<ReportViewMode>("question");
  const [mountedAt] = useState(() => new Date());
  const printing = usePrinting();

  const report = useMemo(
    () => toAdminCampaignReport(campaign, groupsQuery.data, forms),
    [campaign, groupsQuery.data, forms],
  );
  const status = reportStatusOf(campaign, mountedAt);
  // Unknown or missing `person` falls back to the first person.
  const person = report.people.find((item) => item.uuid === search.person) ?? report.people[0];

  function selectPerson(uuid: EmployeeId) {
    void navigate({ search: { person: uuid } });
  }

  const actions = (
    <>
      <Button
        className="
          min-h-11
          lg:min-h-10
        "
        disabled={report.people.length === 0}
        onClick={() => downloadCsv(csvFileName(campaign), toCsv(CAMPAIGN_REPORT_CSV_HEADER, campaignReportCsvRows(report)))}
        size="sm"
        type="button"
      >
        <DocumentExport aria-hidden size="16" />
        Export CSV
      </Button>
      <Button
        className="
          min-h-11
          lg:min-h-10
        "
        disabled={!person}
        onClick={() => globalThis.print()}
        size="sm"
        type="button"
      >
        <Download aria-hidden size="16" />
        Download PDF
      </Button>
      {(status === "CLOSED" || status === "PUBLISHED") && <CampaignPublishAction campaign={campaign} />}
    </>
  );

  return (
    <div className="flex min-w-0 flex-col gap-6 pb-6">
      <CampaignReportHeader actions={actions} campaign={campaign} stats={report.stats} status={status} />

      {groupsQuery.isError && (
        <div className="
          flex flex-wrap items-center gap-3 text-body3 text-neutral-600
          print:hidden
        " role="status">
          Group information couldn't load.
          <button
            className={buttonVariants({ size: "sm", className: "min-h-11 lg:min-h-0" })}
            onClick={() => void groupsQuery.refetch()}
            type="button"
          >
            Retry
          </button>
        </div>
      )}

      {person ? (
        <div className="
          flex flex-col gap-4
          lg:grid lg:grid-cols-[17rem_minmax(0,1fr)] lg:items-start lg:gap-0
          lg:border-t lg:border-[#ECEAF1]
        ">
          <ReportPeopleList
            className="
              hidden
              lg:sticky lg:top-0 lg:flex lg:max-h-[calc(100dvh-8rem)]
              lg:overflow-y-auto lg:border-r lg:border-[#ECEAF1] lg:py-3.5
              lg:pr-3
              print:hidden
            "
            onSelect={selectPerson}
            people={report.people}
            selected={person.uuid}
          />
          <Select
            className="
              lg:hidden
              print:hidden
            "
            label="Person"
            onValueChange={(value) => {
              const picked = report.people.find((item) => item.uuid === value);
              if (picked) {
                selectPerson(picked.uuid);
              }
            }}
            options={report.people.map((item) => ({
              value: item.uuid,
              label: `${item.name} · ${item.received}/${item.expected}`,
            }))}
            triggerClassName="min-h-11"
            value={person.uuid}
          />
          <div className="
            min-w-0
            lg:py-4.5 lg:pl-7
          ">
            <PersonReport
              activeFormKey={search.form}
              mode={mode}
              // Tabs replace the history entry: Back leaves the person, not each tab.
              onFormChange={(key) => void navigate({ search: (previous) => ({ ...previous, form: key }), replace: true })}
              onModeChange={setMode}
              person={person}
              printing={printing}
            />
          </div>
        </div>
      ) : (
        <p className="text-body2 text-neutral-600">No evaluations in this campaign yet.</p>
      )}
    </div>
  );
}

function ReportSkeleton() {
  return (
    <div aria-label="Loading report" className="flex flex-col gap-6" role="status">
      <div className="flex flex-col gap-3">
        <div className="h-4 w-40 animate-pulse rounded-sm bg-primary-100" />
        <div className="
          h-9 w-80 max-w-full animate-pulse rounded-md bg-primary-100
        " />
      </div>
      <div className="
        grid grid-cols-2 gap-3
        md:grid-cols-4
      ">
        {["groups", "people", "forms", "rate"].map((key) => (
          <div className="h-16 animate-pulse rounded-[10px] bg-primary-50" key={key} />
        ))}
      </div>
      <div className="
        flex flex-col gap-4
        lg:grid lg:grid-cols-[17rem_minmax(0,1fr)]
      ">
        <div className="
          hidden flex-col gap-2
          lg:flex
        ">
          {["a", "b", "c", "d"].map((key) => (
            <div className="h-14 animate-pulse rounded-lg bg-primary-50" key={key} />
          ))}
        </div>
        <div className="h-96 animate-pulse rounded-xl bg-primary-50" />
      </div>
    </div>
  );
}
